import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { fetchCurrentUser, type JgsStudentData, type AjgaData } from "@/services/api";
import TwoPaneLayout from "@/components/TwoPaneLayout";
import Sidebar from "@/components/Sidebar";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { useNavigate, useSearchParams } from "react-router-dom";
import { updateMyProfile, updateProfileField } from "@/services/profile";
import { generateRecruitingPlan } from "@/services/pipeline";
import { trackProfileFieldSaved } from "@/analytics/analytics";
import AutosaveInput from "@/components/profile/AutosaveInput";
import AutosaveTextarea from "@/components/profile/AutosaveTextarea";
import HighlightsEditor from "@/components/profile/HighlightsEditor";
import MajorsSelector from "@/components/profile/MajorsSelector";
import ReadOnlyBadge from "@/components/profile/ReadOnlyBadge";
import SportsSelector from "@/components/profile/SportsSelector";
import YouTubePreview from "@/components/profile/YouTubePreview";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { StatCard, SectionHeader } from "@/components/school/SchoolDetailContent";
import {
  ChevronDown,
  GraduationCap,
  User,
  BookOpen,
  Settings,
  Link2,
  Zap,
  CircleAlert,
  CircleCheck,
  Trophy,
  X,
} from "lucide-react";
import { useEndUser } from "@/contexts/EndUserContext";
import { JgsSearchForm } from "@/components/JgsLinkBanner";
import SetPasswordSection from "@/components/SetPasswordSection";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { getSubscription, createPortalSession } from "@/services/payments";
import { computeProfileStrength, filterDismissed, type MissingItem } from "@/utils/profileStrength";
import { US_REGIONS } from "@/components/onboarding/locations";
import { formatEvidence } from "@/components/pipeline/pipelineDesign";
import { PAGE_SUBTITLE_CLASS, PAGE_TITLE_CLASS } from "@/constants/design";

interface MatchingPreferencesSnapshot {
  divisionsOpenTo: string[];
  locationPreferenceMode: string;
  preferredRegions: string[];
  preferredStates: string;
  maxAnnualNetPrice: string;
  schoolPreferencesNotes: string;
}

const emptyMatchingPreferences: MatchingPreferencesSnapshot = {
  divisionsOpenTo: [],
  locationPreferenceMode: "anywhere",
  preferredRegions: [],
  preferredStates: "",
  maxAnnualNetPrice: "",
  schoolPreferencesNotes: "",
};

function matchingPreferencesFromProfile(profile: any): MatchingPreferencesSnapshot {
  return {
    divisionsOpenTo: Array.isArray(profile?.divisionsOpenTo) ? profile.divisionsOpenTo : [],
    locationPreferenceMode: profile?.locationPreferenceMode || "anywhere",
    preferredRegions: Array.isArray(profile?.preferredRegions) ? profile.preferredRegions : [],
    preferredStates: Array.isArray(profile?.preferredStates)
      ? profile.preferredStates.join(", ")
      : "",
    maxAnnualNetPrice:
      typeof profile?.maxAnnualNetPrice === "number" ? String(profile.maxAnnualNetPrice) : "",
    schoolPreferencesNotes: profile?.schoolPreferencesNotes || "",
  };
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const PlayerProfile = () => {
  const navigate = useNavigate();
  const { playerSummary, isSidebarOpen, closeSidebar, chatHistory, refreshUserData } = useEndUser();

  const [loading, setLoading] = useState(true);
  const [email, setEmail] = useState<string>("");
  const [displayName, setDisplayName] = useState<string>("");
  const [cellNumber, setCellNumber] = useState<string>("");
  const [coachReplyEmail, setCoachReplyEmail] = useState<string>("");
  const [isCoachAccount, setIsCoachAccount] = useState<boolean>(false);
  const [gpa, setGpa] = useState<string>("");
  const [weightedGpa, setWeightedGpa] = useState<string>("");
  const [satScore, setSatScore] = useState<string>("");
  const [actScore, setActScore] = useState<string>("");
  const [gradYear, setGradYear] = useState<string>("");
  const [homeState, setHomeState] = useState<string>("");
  const [gender, setGender] = useState<string>("");
  const [onboardingRole, setOnboardingRole] = useState<string>("");
  const [divisionsOpenTo, setDivisionsOpenTo] = useState<string[]>([]);
  const [locationPreferenceMode, setLocationPreferenceMode] = useState<string>("anywhere");
  const [preferredRegions, setPreferredRegions] = useState<string[]>([]);
  const [preferredStates, setPreferredStates] = useState<string>("");
  const [maxAnnualNetPrice, setMaxAnnualNetPrice] = useState<string>("");
  const [schoolPreferencesNotes, setSchoolPreferencesNotes] = useState<string>("");
  const [highlights, setHighlights] = useState<string[]>([]);
  const [swingCoachName, setSwingCoachName] = useState<string>("");
  const [swingCoachContact, setSwingCoachContact] = useState<string>("");
  const [swingVideoUrl, setSwingVideoUrl] = useState<string>("");
  const [preferredMajors, setPreferredMajors] = useState<string[]>([]);
  const [swingSpeed, setSwingSpeed] = useState<string>("");
  const [showClubGolf, setShowClubGolf] = useState<boolean>(false);
  const [personalStatement, setPersonalStatement] = useState<string>("");
  const [characterStories, setCharacterStories] = useState<string>("");
  const [otherSports, setOtherSports] = useState<string[]>([]);
  const [athleticBackground, setAthleticBackground] = useState<string>("");
  const [jgsData, setJgsData] = useState<JgsStudentData | null>(null);
  const [ajgaData, setAjgaData] = useState<AjgaData | null>(null);
  const [ajgaTournamentsOpen, setAjgaTournamentsOpen] = useState(false);
  const [hasPassword, setHasPassword] = useState<boolean | null>(null);
  const [jgsDetailsOpen, setJgsDetailsOpen] = useState(false);
  const [subscription, setSubscription] = useState<any>(null);
  const [dismissedFields, setDismissedFields] = useState<string[]>([]);
  const [savedMatchingPreferences, setSavedMatchingPreferences] =
    useState<MatchingPreferencesSnapshot>(emptyMatchingPreferences);
  const [matchingSaveState, setMatchingSaveState] = useState<"idle" | "saving">("idle");
  const [showUpdateSchoolsDialog, setShowUpdateSchoolsDialog] = useState(false);
  const [updatingSchools, setUpdatingSchools] = useState(false);

  // Section open/closed state — all closed on load, user decides
  const [academicsOpen, setAcademicsOpen] = useState(false);
  const [preferencesOpen, setPreferencesOpen] = useState(false);
  const [wholePlayerOpen, setWholePlayerOpen] = useState(false);
  const [golfOpen, setGolfOpen] = useState(false);
  const [accountOpen, setAccountOpen] = useState(false);

  // Refs for scroll-to-field from Zone 2
  const gpaRef = useRef<HTMLDivElement>(null);
  const satRef = useRef<HTMLDivElement>(null);
  const majorsRef = useRef<HTMLDivElement>(null);
  const highlightsRef = useRef<HTMLDivElement>(null);
  const swingVideoRef = useRef<HTMLDivElement>(null);
  const nameRef = useRef<HTMLDivElement>(null);
  const coachReplyRef = useRef<HTMLDivElement>(null);
  const cellRef = useRef<HTMLDivElement>(null);
  const personalStatementRef = useRef<HTMLDivElement>(null);
  const otherSportsRef = useRef<HTMLDivElement>(null);
  const swingSpeedRef = useRef<HTMLDivElement>(null);

  const fieldRefs: Record<string, React.RefObject<HTMLDivElement | null>> = {
    gpa: gpaRef,
    sat: satRef,
    majors: majorsRef,
    highlights: highlightsRef,
    swingVideo: swingVideoRef,
    name: nameRef,
    cell: cellRef,
    personalStatement: personalStatementRef,
    otherSports: otherSportsRef,
    swingSpeed: swingSpeedRef,
  };

  // Fetch profile-specific data (form fields) — auth already handled by layout
  useEffect(() => {
    (async () => {
      try {
        const me = await fetchCurrentUser();
        setEmail((me as any)?.email || "");
        if (typeof (me as any)?.hasPassword === "boolean") setHasPassword((me as any).hasPassword);
        const meUserType = (me as any)?.userType || "player";
        setIsCoachAccount(meUserType === "coach");
        if (meUserType === "coach") {
          const profiles = (me as any)?.linkedProfiles || [];
          let savedProfile: string | null = null;
          try {
            savedProfile = sessionStorage.getItem("recruit_ai_coach_active_profile");
          } catch {}
          const hasValidSaved =
            savedProfile && profiles.some((p: any) => p.jgsProfileId === savedProfile);
          const activeProfile = hasValidSaved
            ? profiles.find((p: any) => p.jgsProfileId === savedProfile)
            : profiles[0] || null;
          setJgsData(activeProfile?.jgsData || null);
          setAjgaData(activeProfile?.ajgaData || null);
          setDisplayName(activeProfile?.displayName || activeProfile?.jgsData?.player_name || "");
          // Load per-student profile from the enriched linked profile
          const profile = activeProfile?.profile || null;
          if (profile) {
            setCellNumber(profile.cellNumber || "");
            setGpa(typeof profile.gpa === "number" ? String(profile.gpa) : "");
            setWeightedGpa(
              typeof profile.weightedGpa === "number" ? String(profile.weightedGpa) : ""
            );
            setSatScore(typeof profile.satScore === "number" ? String(profile.satScore) : "");
            setActScore(typeof profile.actScore === "number" ? String(profile.actScore) : "");
            setGradYear(profile.gradYear ? String(profile.gradYear) : "");
            setHomeState(profile.homeState || "");
            setGender(profile.gender || "");
            setOnboardingRole(profile.onboardingRole || "");
            const matching = matchingPreferencesFromProfile(profile);
            setDivisionsOpenTo(matching.divisionsOpenTo);
            setLocationPreferenceMode(matching.locationPreferenceMode);
            setPreferredRegions(matching.preferredRegions);
            setPreferredStates(matching.preferredStates);
            setMaxAnnualNetPrice(matching.maxAnnualNetPrice);
            setSchoolPreferencesNotes(matching.schoolPreferencesNotes);
            setSavedMatchingPreferences(matching);
            setHighlights(
              Array.isArray(profile.juniorGolfHighlights) ? profile.juniorGolfHighlights : []
            );
            setSwingCoachName(profile.swingCoachName || "");
            setSwingCoachContact(profile.swingCoachContact || "");
            setSwingVideoUrl(profile.swingVideoUrl || "");
            setPreferredMajors(
              Array.isArray(profile.preferredMajors) ? profile.preferredMajors : []
            );
            setSwingSpeed(typeof profile.swingSpeed === "number" ? String(profile.swingSpeed) : "");
            setPersonalStatement(profile.personalStatement || "");
            setCharacterStories(profile.characterStories || "");
            setOtherSports(Array.isArray(profile.otherSports) ? profile.otherSports : []);
            setAthleticBackground(profile.athleticBackground || "");
          }
        } else {
          setJgsData(me?.jgs_student_data || null);
          setAjgaData(me?.ajga_data || null);
          setShowClubGolf((me as any)?.showClubGolf ?? false);
          const profile = (me as any)?.profile || null;
          setDisplayName(profile?.displayName || me?.jgs_student_data?.player_name || "");
          setDismissedFields((me as any)?.profile?.dismissedProfileFields ?? []);
          if (profile) {
            setCellNumber(profile.cellNumber || "");
            setCoachReplyEmail(profile.coachReplyEmail || "");
            setGpa(typeof profile.gpa === "number" ? String(profile.gpa) : "");
            setWeightedGpa(
              typeof profile.weightedGpa === "number" ? String(profile.weightedGpa) : ""
            );
            setSatScore(typeof profile.satScore === "number" ? String(profile.satScore) : "");
            setActScore(typeof profile.actScore === "number" ? String(profile.actScore) : "");
            setGradYear(profile.gradYear ? String(profile.gradYear) : "");
            setHomeState(profile.homeState || "");
            setGender(profile.gender || "");
            setOnboardingRole(profile.onboardingRole || "");
            const matching = matchingPreferencesFromProfile(profile);
            setDivisionsOpenTo(matching.divisionsOpenTo);
            setLocationPreferenceMode(matching.locationPreferenceMode);
            setPreferredRegions(matching.preferredRegions);
            setPreferredStates(matching.preferredStates);
            setMaxAnnualNetPrice(matching.maxAnnualNetPrice);
            setSchoolPreferencesNotes(matching.schoolPreferencesNotes);
            setSavedMatchingPreferences(matching);
            setHighlights(
              Array.isArray(profile.juniorGolfHighlights) ? profile.juniorGolfHighlights : []
            );
            setSwingCoachName(profile.swingCoachName || "");
            setSwingCoachContact(profile.swingCoachContact || "");
            setSwingVideoUrl(profile.swingVideoUrl || "");
            setPreferredMajors(
              Array.isArray(profile.preferredMajors) ? profile.preferredMajors : []
            );
            setSwingSpeed(typeof profile.swingSpeed === "number" ? String(profile.swingSpeed) : "");
            setPersonalStatement(profile.personalStatement || "");
            setCharacterStories(profile.characterStories || "");
            setOtherSports(Array.isArray(profile.otherSports) ? profile.otherSports : []);
            setAthleticBackground(profile.athleticBackground || "");
          }
        }
      } catch {
        // ignore
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // Fetch subscription data (silently fails for non-individual users)
  useEffect(() => {
    (async () => {
      try {
        const sub = await getSubscription();
        setSubscription(sub);
      } catch {
        // expected for non-individual users
      }
    })();
  }, []);

  // Get the active linked profile for coaches (used when saving profile fields)
  const getLinkedJgsProfile = (): string | undefined => {
    try {
      return sessionStorage.getItem("recruit_ai_coach_active_profile") || undefined;
    } catch {
      return undefined;
    }
  };

  const isLinked = playerSummary.isLinked;

  const { percentage, missingItems } = computeProfileStrength({
    isLinked,
    gpa,
    highlights,
    satScore,
    preferredMajors,
    swingVideoUrl,
    displayName,
    cellNumber,
    personalStatement,
    otherSports,
    swingSpeed,
  });

  const visibleMissingItems = filterDismissed(missingItems, dismissedFields);

  const sectionMissing = {
    academics: visibleMissingItems.filter(i => i.tabTarget === "academics").length,
    golf: visibleMissingItems.filter(i => i.tabTarget === "golf").length,
    wholePlayer: visibleMissingItems.filter(i => i.tabTarget === "whole-player").length,
    account: visibleMissingItems.filter(i => i.tabTarget === "account").length,
  };

  const sectionOpeners: Record<string, () => void> = {
    academics: () => setAcademicsOpen(true),
    golf: () => setGolfOpen(true),
    "whole-player": () => setWholePlayerOpen(true),
    account: () => setAccountOpen(true),
  };

  // Deep links from the Coach Emails page, e.g. /profile?field=coachReplyEmail (ZON-291)
  const [searchParams] = useSearchParams();
  const focusField = searchParams.get("field");
  useEffect(() => {
    if (loading || !focusField) return;
    const ref =
      focusField === "coachReplyEmail"
        ? coachReplyRef
        : focusField === "displayName"
          ? nameRef
          : null;
    if (!ref) return;
    setAccountOpen(true);
    const timer = window.setTimeout(() => {
      ref.current?.scrollIntoView({ behavior: "smooth", block: "center" });
      ref.current?.querySelector<HTMLElement>("input")?.focus();
    }, 300);
    return () => window.clearTimeout(timer);
  }, [loading, focusField]);

  const handleMissingItemClick = (item: MissingItem) => {
    if (!item.tabTarget) {
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }
    sectionOpeners[item.tabTarget]?.();
    const ref = fieldRefs[item.fieldRef];
    setTimeout(() => {
      ref?.current?.scrollIntoView({ behavior: "smooth", block: "center" });
      setTimeout(() => {
        const input = ref?.current?.querySelector<HTMLElement>("input, textarea");
        input?.focus();
      }, 400);
    }, 150);
  };

  const handleResetDismissals = async () => {
    setDismissedFields([]);
    setAcademicsOpen(missingItems.some(i => i.tabTarget === "academics"));
    setGolfOpen(missingItems.some(i => i.tabTarget === "golf"));
    setWholePlayerOpen(missingItems.some(i => i.tabTarget === "whole-player"));
    setAccountOpen(missingItems.some(i => i.tabTarget === "account"));
    try {
      await updateProfileField("dismissedProfileFields", [], getLinkedJgsProfile());
    } catch {
      toast.error("Couldn't reset suggestions. Please try again.");
    }
  };

  const handleDismiss = async (fieldRef: string) => {
    const previous = dismissedFields;
    const updated = [...dismissedFields, fieldRef];
    setDismissedFields(updated);
    try {
      await updateProfileField("dismissedProfileFields", updated, getLinkedJgsProfile());
    } catch {
      setDismissedFields(previous);
      toast.error("Couldn't save your preference. Please try again.");
    }
  };

  const jgsGenderText = String(jgsData?.gender || "").toLowerCase();
  const normalizedJgsGender = /boy|male|men/.test(jgsGenderText)
    ? "male"
    : /girl|female|women/.test(jgsGenderText)
      ? "female"
      : "";
  const sourceMismatches = [
    gradYear &&
    (jgsData as any)?.class_year &&
    String(gradYear) !== String((jgsData as any).class_year)
      ? "graduation year"
      : null,
    homeState &&
    jgsData?.state &&
    homeState.trim().toUpperCase() !== String(jgsData.state).trim().toUpperCase()
      ? "home state"
      : null,
    gender && normalizedJgsGender && gender !== normalizedJgsGender ? "gender" : null,
  ].filter(Boolean) as string[];

  const currentMatchingPreferences: MatchingPreferencesSnapshot = {
    divisionsOpenTo,
    locationPreferenceMode,
    preferredRegions,
    preferredStates,
    maxAnnualNetPrice,
    schoolPreferencesNotes,
  };
  const matchingPreferencesDirty =
    JSON.stringify(currentMatchingPreferences) !== JSON.stringify(savedMatchingPreferences);

  const restoreMatchingPreferences = () => {
    setDivisionsOpenTo(savedMatchingPreferences.divisionsOpenTo);
    setLocationPreferenceMode(savedMatchingPreferences.locationPreferenceMode);
    setPreferredRegions(savedMatchingPreferences.preferredRegions);
    setPreferredStates(savedMatchingPreferences.preferredStates);
    setMaxAnnualNetPrice(savedMatchingPreferences.maxAnnualNetPrice);
    setSchoolPreferencesNotes(savedMatchingPreferences.schoolPreferencesNotes);
  };

  const handleLocationPreferenceChange = (next: string) => {
    setLocationPreferenceMode(next);
    if (next === "anywhere") {
      setPreferredRegions([]);
      setPreferredStates("");
    } else if (next === "regions") {
      setPreferredStates("");
    } else {
      setPreferredRegions([]);
      if ((next === "home" || next === "nearby") && !preferredStates.trim() && homeState) {
        setPreferredStates(homeState.trim().toUpperCase());
      }
    }
  };

  const saveMatchingPreferences = async () => {
    if (divisionsOpenTo.length === 0) {
      toast.error("Choose at least one college golf division.");
      return;
    }
    if (locationPreferenceMode === "regions" && preferredRegions.length === 0) {
      toast.error("Choose at least one region.");
      return;
    }

    const states = [
      ...new Set(
        preferredStates
          .split(",")
          .map(item => item.trim().toUpperCase())
          .filter(Boolean)
      ),
    ];
    if (states.some(state => !/^[A-Z]{2}$/.test(state))) {
      toast.error("Use two-letter state abbreviations separated by commas.");
      return;
    }
    if (locationPreferenceMode === "specific" && states.length === 0) {
      toast.error("Add at least one state.");
      return;
    }
    const parsedPrice = maxAnnualNetPrice.trim() ? Number(maxAnnualNetPrice) : null;
    if (
      parsedPrice !== null &&
      (!Number.isFinite(parsedPrice) || parsedPrice < 0 || parsedPrice > 100000)
    ) {
      toast.error("Enter an annual net price from $0 to $100,000.");
      return;
    }

    const normalizedStates =
      locationPreferenceMode === "anywhere" || locationPreferenceMode === "regions"
        ? []
        : locationPreferenceMode === "home" && homeState
          ? [homeState.trim().toUpperCase()]
          : states;
    const normalizedRegions = locationPreferenceMode === "regions" ? preferredRegions : [];
    setMatchingSaveState("saving");
    try {
      await updateMyProfile({
        divisionsOpenTo,
        locationPreferenceMode: locationPreferenceMode as any,
        preferredRegions: normalizedRegions,
        preferredStates: normalizedStates,
        maxAnnualNetPrice: parsedPrice,
        schoolPreferencesNotes: schoolPreferencesNotes.trim() || null,
        linkedJgsProfile: getLinkedJgsProfile(),
      });
      const saved = {
        divisionsOpenTo,
        locationPreferenceMode,
        preferredRegions: normalizedRegions,
        preferredStates: normalizedStates.join(", "),
        maxAnnualNetPrice: parsedPrice === null ? "" : String(parsedPrice),
        schoolPreferencesNotes: schoolPreferencesNotes.trim(),
      };
      setPreferredRegions(saved.preferredRegions);
      setPreferredStates(saved.preferredStates);
      setMaxAnnualNetPrice(saved.maxAnnualNetPrice);
      setSchoolPreferencesNotes(saved.schoolPreferencesNotes);
      setSavedMatchingPreferences(saved);
      setShowUpdateSchoolsDialog(true);
      toast.success("Matching preferences saved.");
      await refreshUserData();
    } catch (error: any) {
      toast.error(String(error?.message || "Matching preferences could not be saved."));
    } finally {
      setMatchingSaveState("idle");
    }
  };

  const updateMySchoolsFromPreferences = async () => {
    setUpdatingSchools(true);
    try {
      const result = await generateRecruitingPlan({ refreshRecommendations: true });
      setShowUpdateSchoolsDialog(false);
      const changed = (result.replaced || 0) + result.added;
      toast.success(
        changed > 0
          ? "My Schools now reflects your new preferences."
          : "Your current My Schools list already fits these preferences."
      );
    } catch (error: any) {
      toast.error(String(error?.message || "My Schools could not be updated."));
    } finally {
      setUpdatingSchools(false);
    }
  };

  if (loading) {
    return <div className="p-6">Loading…</div>;
  }

  return (
    <TwoPaneLayout
      isSidebarOpen={isSidebarOpen}
      onCloseSidebar={closeSidebar}
      sidebar={
        <Sidebar
          chatHistory={chatHistory}
          onNewChat={() => navigate("/chat")}
          onClose={closeSidebar}
          onSelectChat={id => navigate(`/chat?thread=${id}`)}
          isLinked={isLinked}
        />
      }
      main={
        <div className="relative flex-1 overflow-y-auto">
          <div className="max-w-3xl mx-auto p-4 md:p-6 space-y-6">
            <div>
              <h1 className={PAGE_TITLE_CLASS}>Profile</h1>
              <p className={PAGE_SUBTITLE_CLASS}>
                Your profile powers coach emails and school matching — the more complete it is, the
                better your results.
              </p>
            </div>

            {/* ── Zone 1 — Player Snapshot ── */}
            <section className="space-y-2.5">
              <SectionHeader icon={<Zap className="h-4 w-4" />}>Player Snapshot</SectionHeader>

              {isLinked ? (
                <>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                    <StatCard
                      value={typeof jgsData?.rank === "number" ? `#${jgsData.rank}` : "—"}
                      label="JGS rank"
                      accent={typeof jgsData?.rank === "number"}
                    />
                    <StatCard
                      value={
                        typeof (jgsData as any)?.class_rank === "number"
                          ? `#${(jgsData as any).class_rank}`
                          : "—"
                      }
                      label="Class rank"
                    />
                    <StatCard
                      value={
                        (jgsData?.scoring_differential ?? "") !== "" &&
                        Number.isFinite(Number(jgsData?.scoring_differential))
                          ? formatEvidence(Number(jgsData?.scoring_differential))
                          : "—"
                      }
                      label="Differential"
                    />
                    <StatCard
                      value={
                        (jgsData as any)?.class_year ? String((jgsData as any).class_year) : "—"
                      }
                      label="Class"
                    />
                  </div>

                  <Collapsible open={jgsDetailsOpen} onOpenChange={setJgsDetailsOpen}>
                    <CollapsibleTrigger className="flex items-center gap-2 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors py-1">
                      <ChevronDown
                        className={`h-4 w-4 transition-transform ${jgsDetailsOpen ? "rotate-180" : "-rotate-90"}`}
                      />
                      JGS details
                    </CollapsibleTrigger>
                    <CollapsibleContent>
                      <div className="flex flex-col mt-1">
                        <div className="text-sm flex items-start py-2.5 md:py-3 border-t border-border">
                          <span className="text-muted-foreground w-36 shrink-0">Gender</span>
                          <span
                            className={
                              jgsData?.gender ? "text-foreground" : "italic text-muted-foreground"
                            }
                          >
                            {jgsData?.gender || "—"}
                          </span>
                        </div>
                        <div className="text-sm flex items-start py-2.5 md:py-3 border-t border-border">
                          <span className="text-muted-foreground w-36 shrink-0">
                            State/Hometown
                          </span>
                          <span
                            className={
                              jgsData?.hometown || jgsData?.state
                                ? "text-foreground"
                                : "italic text-muted-foreground"
                            }
                          >
                            {[
                              String(jgsData?.hometown ?? "").trim(),
                              String(jgsData?.state ?? "").trim(),
                            ]
                              .filter(Boolean)
                              .join(", ") || "—"}
                          </span>
                        </div>
                        <div className="text-sm flex items-start py-2.5 md:py-3 border-t border-border">
                          <span className="text-muted-foreground w-36 shrink-0">Events Played</span>
                          <span
                            className={
                              typeof jgsData?.events === "number"
                                ? "text-foreground"
                                : "italic text-muted-foreground"
                            }
                          >
                            {typeof jgsData?.events === "number" ? jgsData.events : "—"}
                          </span>
                        </div>
                        <div className="text-sm flex items-start py-2.5 md:py-3 border-t border-border">
                          <span className="text-muted-foreground w-36 shrink-0">
                            Strength of Tournament
                          </span>
                          <span
                            className={
                              typeof jgsData?.strength_of_tournament === "number"
                                ? "text-foreground"
                                : "italic text-muted-foreground"
                            }
                          >
                            {typeof jgsData?.strength_of_tournament === "number"
                              ? jgsData.strength_of_tournament
                              : "—"}
                          </span>
                        </div>
                      </div>
                    </CollapsibleContent>
                  </Collapsible>
                  <div className="rounded-lg border border-border bg-surface px-3 py-2 text-xs leading-relaxed text-forest-soft">
                    Golf-fit assessments use the verified JGS class rank, scoring differential, and
                    class year shown here. Editable profile details below provide context but never
                    replace verified JGS performance data.
                    {sourceMismatches.length > 0 && (
                      <span className="mt-1 block font-semibold text-amber-text">
                        Your profile and JGS currently differ on {sourceMismatches.join(", ")}. We
                        keep both sources visible so you can correct the profile or relink JGS.
                      </span>
                    )}
                  </div>
                </>
              ) : (
                <div className="rounded-lg border-2 border-primary/30 bg-primary/5 p-5">
                  <div className="flex items-center gap-2 mb-2">
                    <Link2 className="h-5 w-5 text-primary-text" />
                    <h3 className="font-semibold">Link Your JGS Profile</h3>
                  </div>
                  <p className="text-sm text-muted-foreground mb-4">
                    Connect to unlock school matching and personalized recommendations.
                  </p>
                  <JgsSearchForm
                    onLinkSuccess={() => {
                      toast.success("Profile linked! Refreshing your data...");
                      window.location.reload();
                    }}
                    source="profile"
                    initialName={displayName || undefined}
                    initialState={homeState || undefined}
                    initialGender={gender || undefined}
                  />
                </div>
              )}
            </section>

            {/* ── AJGA Data ── */}
            {ajgaData && (
              <section className="space-y-3">
                <SectionHeader icon={<Trophy className="h-4 w-4" />}>AJGA Data</SectionHeader>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                  <StatCard
                    value={typeof ajgaData.rank === "number" ? `#${ajgaData.rank}` : "—"}
                    label="AJGA Rank"
                    accent
                  />
                  <StatCard
                    value={
                      typeof ajgaData.events_played === "number"
                        ? String(ajgaData.events_played)
                        : "—"
                    }
                    label="Events"
                  />
                  <StatCard
                    value={
                      typeof ajgaData.total_points === "number"
                        ? String(ajgaData.total_points)
                        : "—"
                    }
                    label="Total points"
                  />
                  <StatCard
                    value={
                      typeof ajgaData.avg_points === "number" ? ajgaData.avg_points.toFixed(1) : "—"
                    }
                    label="Avg points"
                  />
                </div>

                {ajgaData.tournaments && ajgaData.tournaments.length > 0 && (
                  <Collapsible open={ajgaTournamentsOpen} onOpenChange={setAjgaTournamentsOpen}>
                    <CollapsibleTrigger className="flex items-center gap-2 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors w-full py-1">
                      <ChevronDown
                        className={`h-4 w-4 transition-transform ${ajgaTournamentsOpen ? "rotate-180" : "-rotate-90"}`}
                      />
                      Tournament Results ({ajgaData.tournaments.length})
                    </CollapsibleTrigger>
                    <CollapsibleContent>
                      <div className="mt-2 space-y-2">
                        {ajgaData.tournaments.map((t, i) => (
                          <div
                            key={i}
                            className="rounded-md border bg-muted/50 px-3 py-2.5 text-sm"
                          >
                            <div className="font-medium">{t.event || "Unknown Event"}</div>
                            <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1 text-xs text-muted-foreground">
                              {t.start_date && (
                                <span>{new Date(t.start_date).toLocaleDateString()}</span>
                              )}
                              {t.place != null && (
                                <span>
                                  {t.place}
                                  {t.participant_count ? ` / ${t.participant_count}` : ""}
                                  <span className="text-muted-foreground/60 ml-1">place</span>
                                </span>
                              )}
                              {t.scores && <span>{t.scores}</span>}
                            </div>
                          </div>
                        ))}
                      </div>
                    </CollapsibleContent>
                  </Collapsible>
                )}
              </section>
            )}

            {/* ── Zone 2 — Profile Strength ── */}
            <section className="space-y-3">
              <button
                type="button"
                onClick={handleResetDismissals}
                className="w-full text-left group"
              >
                <div className="flex items-center justify-between mb-2">
                  <SectionHeader>Profile Strength</SectionHeader>
                  <span className="text-sm font-semibold tabular-nums group-hover:text-primary-text transition-colors">
                    {percentage}%
                  </span>
                </div>
                <Progress value={percentage} className="h-2" />
              </button>

              {visibleMissingItems.length === 0 ? (
                missingItems.length === 0 ? (
                  <div className="flex items-center gap-2 text-sm text-green-600">
                    <CircleCheck className="h-4 w-4" />
                    <span>Your profile is complete — coaches see the full picture.</span>
                  </div>
                ) : (
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <CircleCheck className="h-4 w-4" />
                    <span>All suggestions reviewed. Profile is at {percentage}%.</span>
                  </div>
                )
              ) : (
                <div className="space-y-2">
                  {/* Top priority — one prominent action */}
                  <div className="w-full flex items-start gap-3 rounded-lg border border-primary/30 bg-primary/5 px-4 py-3 hover:bg-primary/10 transition-colors">
                    <button
                      type="button"
                      onClick={() => handleMissingItemClick(visibleMissingItems[0])}
                      className="flex items-start gap-3 flex-1 min-w-0 text-left"
                    >
                      <CircleAlert className="h-4 w-4 mt-0.5 shrink-0 text-primary-text" />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-semibold">
                            {visibleMissingItems[0].label}
                          </span>
                          <Badge variant="secondary" className="text-primary-text">
                            +{visibleMissingItems[0].weight}%
                          </Badge>
                        </div>
                        <p className="text-xs text-muted-foreground mt-0.5">
                          {visibleMissingItems[0].message}
                        </p>
                      </div>
                      <span className="text-xs font-semibold text-primary-text shrink-0 mt-0.5">
                        Go →
                      </span>
                    </button>
                    <button
                      type="button"
                      onClick={e => {
                        e.stopPropagation();
                        handleDismiss(visibleMissingItems[0].fieldRef);
                      }}
                      className="text-muted-foreground hover:text-foreground transition-colors shrink-0 ml-1"
                      aria-label={`Dismiss ${visibleMissingItems[0].label}`}
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                  </div>

                  {/* Remaining items — collapsed by default */}
                  {visibleMissingItems.length > 1 && (
                    <Collapsible>
                      <CollapsibleTrigger className="w-full flex items-center justify-between px-3 py-2 rounded-md text-sm text-muted-foreground hover:bg-muted/50 transition-colors [&[data-state=open]>svg]:rotate-180">
                        <span>
                          {visibleMissingItems.length - 1} more{" "}
                          {visibleMissingItems.length - 1 === 1 ? "field" : "fields"} would also
                          help
                        </span>
                        <ChevronDown className="h-3.5 w-3.5 transition-transform duration-200" />
                      </CollapsibleTrigger>
                      <CollapsibleContent>
                        <div className="space-y-1 mt-1">
                          {visibleMissingItems.slice(1).map(item => (
                            <div
                              key={item.fieldRef}
                              className="w-full flex items-center gap-3 rounded-md px-3 py-2 hover:bg-muted/50 transition-colors"
                            >
                              <button
                                type="button"
                                onClick={() => handleMissingItemClick(item)}
                                className="flex items-center gap-3 flex-1 min-w-0 text-left"
                              >
                                <div className="h-1.5 w-1.5 rounded-full bg-amber-400 shrink-0" />
                                <span className="text-sm text-muted-foreground flex-1">
                                  {item.label}
                                </span>
                                <Badge variant="secondary">+{item.weight}%</Badge>
                              </button>
                              <button
                                type="button"
                                onClick={e => {
                                  e.stopPropagation();
                                  handleDismiss(item.fieldRef);
                                }}
                                className="text-muted-foreground hover:text-foreground transition-colors ml-1 p-1"
                                aria-label={`Dismiss ${item.label}`}
                              >
                                <X className="h-3 w-3" />
                              </button>
                            </div>
                          ))}
                        </div>
                      </CollapsibleContent>
                    </Collapsible>
                  )}
                </div>
              )}
            </section>

            {/* ── School matching preferences ── */}
            <Collapsible
              open={preferencesOpen}
              onOpenChange={setPreferencesOpen}
              className="overflow-hidden rounded-xl border border-border bg-white"
            >
              <CollapsibleTrigger className="flex w-full items-start justify-between gap-4 px-4 py-4 text-left transition-colors hover:bg-muted/30 [&[data-state=open]>svg:last-child]:rotate-180 sm:px-5">
                <div className="flex min-w-0 items-start gap-3">
                  <span className="mt-0.5 grid h-9 w-9 shrink-0 place-items-center rounded-xl bg-primary/10 text-primary-text">
                    <GraduationCap className="h-4 w-4" />
                  </span>
                  <span>
                    <span className="block text-sm font-semibold">My Schools matching</span>
                    <span className="mt-1 block text-xs leading-relaxed text-muted-foreground">
                      Choose what should shape the programs we recommend.
                    </span>
                  </span>
                </div>
                <span className="flex shrink-0 items-center gap-2">
                  {matchingPreferencesDirty && (
                    <span className="rounded-full bg-amber-soft px-2 py-1 text-[11px] font-semibold text-amber-text">
                      Unsaved
                    </span>
                  )}
                  <span className="hidden text-xs font-semibold text-primary-text sm:inline">
                    {preferencesOpen ? "Close" : "Edit"}
                  </span>
                  <ChevronDown className="h-4 w-4 text-muted-foreground transition-transform duration-200" />
                </span>
              </CollapsibleTrigger>
              <CollapsibleContent>
                <div className="space-y-6 border-t border-primary/15 bg-background px-4 pb-5 pt-5 sm:px-5">
                  <div className="rounded-xl border border-lime-border bg-lime-soft px-3.5 py-3 text-xs leading-relaxed text-forest-soft">
                    Save these preferences first. Then you can decide whether to update My Schools.
                    Schools you added, noted, contacted, or moved forward will always stay.
                  </div>

                  <div>
                    <p className="mb-2 text-xs font-semibold text-foreground">
                      Which college golf divisions would you consider?
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {["D1", "D2", "D3", "NAIA", "NJCAA"].map(division => {
                        const selected = divisionsOpenTo.includes(division);
                        return (
                          <button
                            key={division}
                            type="button"
                            aria-pressed={selected}
                            onClick={() => {
                              const next = selected
                                ? divisionsOpenTo.filter(item => item !== division)
                                : [...divisionsOpenTo, division];
                              setDivisionsOpenTo(next);
                            }}
                            className={`rounded-full border px-3 py-2 text-xs font-semibold transition ${selected ? "border-primary bg-primary text-primary-foreground" : "border-border bg-background text-muted-foreground hover:border-primary/50 hover:text-foreground"}`}
                          >
                            {division}
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  <div className="space-y-4">
                    <label className="block space-y-1.5 text-xs font-semibold text-foreground">
                      Where should we look?
                      <select
                        value={locationPreferenceMode}
                        onChange={event => handleLocationPreferenceChange(event.target.value)}
                        className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm text-foreground"
                      >
                        <option value="anywhere">Anywhere</option>
                        <option value="home">My home state</option>
                        <option value="nearby">Home + nearby states</option>
                        <option value="regions">Choose regions</option>
                        <option value="specific">Choose specific states</option>
                      </select>
                    </label>

                    {locationPreferenceMode === "regions" && (
                      <div>
                        <p className="mb-2 text-xs text-muted-foreground">
                          Choose every region you want included.
                        </p>
                        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                          {US_REGIONS.map(region => {
                            const selected = preferredRegions.includes(region.name);
                            return (
                              <button
                                key={region.name}
                                type="button"
                                aria-pressed={selected}
                                onClick={() =>
                                  setPreferredRegions(current =>
                                    selected
                                      ? current.filter(item => item !== region.name)
                                      : [...current, region.name]
                                  )
                                }
                                className={`rounded-xl border px-3 py-2.5 text-left transition ${selected ? "border-primary bg-primary/10 text-foreground" : "border-border bg-background text-muted-foreground hover:border-primary/40"}`}
                              >
                                <span className="block text-xs font-semibold">{region.name}</span>
                                <span className="mt-0.5 block text-[11px] leading-relaxed opacity-75">
                                  {region.states.join(", ")}
                                </span>
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {(locationPreferenceMode === "specific" ||
                      locationPreferenceMode === "nearby") && (
                      <label className="block space-y-1.5 text-xs font-semibold text-foreground">
                        {locationPreferenceMode === "nearby"
                          ? "Home and nearby states to include"
                          : "States to include"}
                        <Input
                          value={preferredStates}
                          onChange={event => setPreferredStates(event.target.value)}
                          placeholder="NC, SC, VA"
                          className="text-sm font-normal"
                        />
                        <span className="block font-normal text-muted-foreground">
                          Use two-letter abbreviations separated by commas.
                        </span>
                      </label>
                    )}
                  </div>

                  <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    <label className="block space-y-1.5 text-xs font-semibold text-foreground">
                      Maximum annual net price
                      <Input
                        value={maxAnnualNetPrice}
                        onChange={event => setMaxAnnualNetPrice(event.target.value)}
                        inputMode="numeric"
                        placeholder="No limit"
                        className="text-sm font-normal"
                      />
                      <span className="block font-normal text-muted-foreground">
                        After grants and scholarships. Optional.
                      </span>
                    </label>
                    <label className="block space-y-1.5 text-xs font-semibold text-foreground">
                      What matters to you in a college?
                      <textarea
                        value={schoolPreferencesNotes}
                        onChange={event =>
                          setSchoolPreferencesNotes(event.target.value.slice(0, 2000))
                        }
                        rows={4}
                        placeholder="School size, setting, culture, support, or anything else that matters."
                        className="flex min-h-[92px] w-full resize-y rounded-md border border-input bg-background px-3 py-2 text-sm font-normal ring-offset-background placeholder:text-muted-foreground/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                      />
                    </label>
                  </div>

                  <div className="flex flex-col gap-3 border-t border-border pt-4 sm:flex-row sm:items-center sm:justify-between">
                    <p className="text-xs text-muted-foreground">
                      Other profile sections still save automatically.
                    </p>
                    <div className="flex gap-2">
                      {matchingPreferencesDirty && (
                        <Button
                          type="button"
                          variant="outline"
                          onClick={restoreMatchingPreferences}
                        >
                          Cancel changes
                        </Button>
                      )}
                      <Button
                        type="button"
                        onClick={() => void saveMatchingPreferences()}
                        disabled={!matchingPreferencesDirty || matchingSaveState === "saving"}
                      >
                        {matchingSaveState === "saving" ? "Saving…" : "Save matching preferences"}
                      </Button>
                    </div>
                  </div>
                </div>
              </CollapsibleContent>
            </Collapsible>

            <AlertDialog open={showUpdateSchoolsDialog} onOpenChange={setShowUpdateSchoolsDialog}>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Update My Schools with these preferences?</AlertDialogTitle>
                  <AlertDialogDescription className="leading-relaxed">
                    We’ll rebuild your recommendations around what you just saved. Schools you added
                    yourself, made notes on, contacted, visited, or otherwise moved forward will
                    stay.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel disabled={updatingSchools}>
                    Keep my current list
                  </AlertDialogCancel>
                  <AlertDialogAction
                    disabled={updatingSchools}
                    onClick={event => {
                      event.preventDefault();
                      void updateMySchoolsFromPreferences();
                    }}
                  >
                    {updatingSchools ? "Updating…" : "Update My Schools"}
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>

            {/* ── Academics ── */}
            <Collapsible
              open={academicsOpen}
              onOpenChange={setAcademicsOpen}
              className="rounded-lg border border-border"
            >
              <CollapsibleTrigger className="flex w-full items-center justify-between px-4 py-3 hover:bg-muted/30 transition-colors [&[data-state=open]>svg:last-child]:rotate-180">
                <div className="flex items-center gap-2">
                  <GraduationCap className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm font-semibold">Academics</span>
                </div>
                <div className="flex items-center gap-2">
                  {sectionMissing.academics === 0 ? (
                    <Badge variant="secondary" className="text-xs text-primary-text bg-primary/10">
                      Complete
                    </Badge>
                  ) : (
                    <Badge variant="secondary" className="text-xs">
                      {sectionMissing.academics} missing
                    </Badge>
                  )}
                  <ChevronDown className="h-4 w-4 text-muted-foreground transition-transform duration-200" />
                </div>
              </CollapsibleTrigger>
              <CollapsibleContent>
                <div className="px-4 pb-4 pt-4 space-y-4 border-t border-border">
                  <p className="text-xs text-muted-foreground">
                    Academic information improves fit scoring at schools.
                  </p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div ref={gpaRef}>
                      <AutosaveInput
                        label="Unweighted GPA (legacy)"
                        placeholder="3.8"
                        value={gpa}
                        onChange={setGpa}
                        type="text"
                        inputMode="decimal"
                        validate={val => {
                          const num = Number(val);
                          if (String(val ?? "").trim() === "") return null;
                          if (!Number.isFinite(num) || num < 0 || num > 4) {
                            return "GPA must be between 0.0 and 4.0";
                          }
                          return null;
                        }}
                        onSave={async v => {
                          const payload = v === null ? null : Number(v);
                          await updateProfileField("gpa", payload as any, getLinkedJgsProfile());
                          trackProfileFieldSaved("gpa");
                        }}
                      />
                    </div>
                    <AutosaveInput
                      label="Weighted GPA"
                      placeholder="4.3"
                      value={weightedGpa}
                      onChange={setWeightedGpa}
                      type="text"
                      inputMode="decimal"
                      validate={value => {
                        if (!String(value || "").trim()) return null;
                        const score = Number(value);
                        return Number.isFinite(score) && score >= 0 && score <= 100
                          ? null
                          : "Weighted GPA must be between 0 and 100";
                      }}
                      onSave={async value =>
                        updateProfileField(
                          "weightedGpa",
                          value ? Number(value) : null,
                          getLinkedJgsProfile()
                        )
                      }
                    />
                    <div ref={satRef}>
                      <AutosaveInput
                        label="SAT Score"
                        placeholder="1450"
                        value={satScore}
                        onChange={setSatScore}
                        type="text"
                        inputMode="numeric"
                        validate={val => {
                          const num = Number(val);
                          if (String(val ?? "").trim() === "") return null;
                          if (!Number.isFinite(num) || num < 400 || num > 1600) {
                            return "SAT must be between 400 and 1600";
                          }
                          return null;
                        }}
                        onSave={async v => {
                          const payload = v === null ? null : Number(v);
                          await updateProfileField(
                            "satScore",
                            payload as any,
                            getLinkedJgsProfile()
                          );
                          trackProfileFieldSaved("satScore");
                        }}
                      />
                    </div>
                    <AutosaveInput
                      label="ACT Score"
                      placeholder="32"
                      value={actScore}
                      onChange={setActScore}
                      type="text"
                      inputMode="numeric"
                      validate={value => {
                        if (!String(value || "").trim()) return null;
                        const score = Number(value);
                        return Number.isFinite(score) && score >= 1 && score <= 36
                          ? null
                          : "ACT must be between 1 and 36";
                      }}
                      onSave={async value =>
                        updateProfileField(
                          "actScore",
                          value ? Number(value) : null,
                          getLinkedJgsProfile()
                        )
                      }
                    />
                  </div>
                  <div ref={majorsRef}>
                    <MajorsSelector
                      items={preferredMajors}
                      onChange={setPreferredMajors}
                      onSave={async arr => {
                        await updateProfileField("preferredMajors", arr, getLinkedJgsProfile());
                        trackProfileFieldSaved("preferredMajors");
                      }}
                    />
                  </div>
                </div>
              </CollapsibleContent>
            </Collapsible>

            {/* ── Whole Player ── */}
            <Collapsible
              open={wholePlayerOpen}
              onOpenChange={setWholePlayerOpen}
              className="rounded-lg border border-border"
            >
              <CollapsibleTrigger className="flex w-full items-center justify-between px-4 py-3 hover:bg-muted/30 transition-colors [&[data-state=open]>svg:last-child]:rotate-180">
                <div className="flex items-center gap-2">
                  <BookOpen className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm font-semibold">Your story</span>
                </div>
                <div className="flex items-center gap-2">
                  {sectionMissing.wholePlayer === 0 ? (
                    <Badge variant="secondary" className="text-xs text-primary-text bg-primary/10">
                      Complete
                    </Badge>
                  ) : (
                    <Badge variant="secondary" className="text-xs">
                      {sectionMissing.wholePlayer} missing
                    </Badge>
                  )}
                  <ChevronDown className="h-4 w-4 text-muted-foreground transition-transform duration-200" />
                </div>
              </CollapsibleTrigger>
              <CollapsibleContent>
                <div className="px-4 pb-4 pt-4 space-y-4 border-t border-border">
                  <p className="text-xs text-muted-foreground">
                    This information helps golfrecruit.ai write stronger chats, emails, and resumes
                    with real personality and context.
                  </p>

                  <AutosaveTextarea
                    label="My story"
                    placeholder="Anything that gives coaches context about your character, adversity, growth, journey in golf, leadership, activities, work, or community involvement."
                    value={characterStories}
                    rows={5}
                    helperText="Optional, but useful when AI drafts more memorable and human outreach. You can include leadership, activities, work, or community involvement here too."
                    onChange={setCharacterStories}
                    onSave={async v => {
                      await updateProfileField("characterStories", v, getLinkedJgsProfile());
                      trackProfileFieldSaved("characterStories");
                    }}
                  />

                  <div ref={personalStatementRef}>
                    <AutosaveTextarea
                      label="Why I Play Golf"
                      placeholder="What keeps you committed to golf, and what do you love about competing?"
                      value={personalStatement}
                      rows={4}
                      helperText="This gives AI a real personal angle for coach outreach."
                      onChange={setPersonalStatement}
                      onSave={async v => {
                        await updateProfileField("personalStatement", v, getLinkedJgsProfile());
                        trackProfileFieldSaved("personalStatement");
                      }}
                    />
                  </div>

                  <div ref={otherSportsRef}>
                    <SportsSelector
                      items={otherSports}
                      onChange={setOtherSports}
                      onSave={async arr => {
                        await updateProfileField(
                          "otherSports",
                          Array.isArray(arr) ? arr : [],
                          getLinkedJgsProfile()
                        );
                        trackProfileFieldSaved("otherSports");
                      }}
                    />
                  </div>

                  <AutosaveTextarea
                    label="Athletic background"
                    placeholder="Share training background, speed or strength work, competitive history, or anything that shows your broader athletic profile."
                    value={athleticBackground}
                    rows={5}
                    helperText="Optional athletic context that can strengthen chats and coach emails."
                    onChange={setAthleticBackground}
                    onSave={async v => {
                      await updateProfileField("athleticBackground", v, getLinkedJgsProfile());
                      trackProfileFieldSaved("athleticBackground");
                    }}
                  />
                </div>
              </CollapsibleContent>
            </Collapsible>

            {/* ── Golf ── */}
            <Collapsible
              open={golfOpen}
              onOpenChange={setGolfOpen}
              className="rounded-lg border border-border"
            >
              <CollapsibleTrigger className="flex w-full items-center justify-between px-4 py-3 hover:bg-muted/30 transition-colors [&[data-state=open]>svg:last-child]:rotate-180">
                <div className="flex items-center gap-2">
                  <Trophy className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm font-semibold">Golf</span>
                </div>
                <div className="flex items-center gap-2">
                  {sectionMissing.golf === 0 ? (
                    <Badge variant="secondary" className="text-xs text-primary-text bg-primary/10">
                      Complete
                    </Badge>
                  ) : (
                    <Badge variant="secondary" className="text-xs">
                      {sectionMissing.golf} missing
                    </Badge>
                  )}
                  <ChevronDown className="h-4 w-4 text-muted-foreground transition-transform duration-200" />
                </div>
              </CollapsibleTrigger>
              <CollapsibleContent>
                <div className="px-4 pb-4 pt-4 space-y-4 border-t border-border">
                  <p className="text-xs text-muted-foreground">
                    Golf performance data and development info for coach outreach.
                  </p>

                  <div ref={highlightsRef}>
                    <HighlightsEditor
                      label="Junior golf highlights"
                      items={highlights}
                      onChange={setHighlights}
                      onSave={async arr => {
                        await updateProfileField(
                          "juniorGolfHighlights",
                          Array.isArray(arr) ? arr : [],
                          getLinkedJgsProfile()
                        );
                        trackProfileFieldSaved("juniorGolfHighlights");
                      }}
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <AutosaveInput
                        label="Swing coach name"
                        placeholder="Coach name"
                        value={swingCoachName}
                        onChange={setSwingCoachName}
                        allowClear
                        onSave={async v => {
                          await updateProfileField(
                            "swingCoachName",
                            v === null ? null : v,
                            getLinkedJgsProfile()
                          );
                          trackProfileFieldSaved("swingCoachName");
                        }}
                      />
                    </div>
                    <AutosaveInput
                      label="Swing coach contact"
                      placeholder="coach@example.com"
                      value={swingCoachContact}
                      onChange={setSwingCoachContact}
                      allowClear
                      onSave={async v => {
                        await updateProfileField(
                          "swingCoachContact",
                          v === null ? null : v,
                          getLinkedJgsProfile()
                        );
                        trackProfileFieldSaved("swingCoachContact");
                      }}
                    />
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div ref={swingVideoRef}>
                      <AutosaveInput
                        label="Link to swing video"
                        placeholder="https://www.youtube.com/watch?v=..."
                        value={swingVideoUrl}
                        onChange={setSwingVideoUrl}
                        allowClear
                        validate={val => {
                          const trimmed = String(val ?? "").trim();
                          if (trimmed === "") return null;
                          if (!trimmed.startsWith("https://"))
                            return "URL must start with https://";
                          return null;
                        }}
                        onSave={async v => {
                          await updateProfileField(
                            "swingVideoUrl",
                            v === null ? null : v,
                            getLinkedJgsProfile()
                          );
                          trackProfileFieldSaved("swingVideoUrl");
                        }}
                      />
                      <YouTubePreview url={swingVideoUrl} />
                    </div>

                    <div ref={swingSpeedRef}>
                      <AutosaveInput
                        label="Swing Speed (mph)"
                        placeholder="108"
                        value={swingSpeed}
                        onChange={setSwingSpeed}
                        type="text"
                        inputMode="numeric"
                        validate={val => {
                          const trimmed = String(val ?? "").trim();
                          if (trimmed === "") return null;
                          const num = Number(trimmed);
                          if (!Number.isFinite(num) || num < 50 || num > 180) {
                            return "Swing speed must be between 50 and 180 mph";
                          }
                          return null;
                        }}
                        onSave={async v => {
                          const payload = v === null ? null : Number(v);
                          await updateProfileField(
                            "swingSpeed",
                            payload as any,
                            getLinkedJgsProfile()
                          );
                          trackProfileFieldSaved("swingSpeed");
                        }}
                      />
                    </div>
                  </div>

                  {/* Club Golf Preference */}
                  <div className="flex items-center justify-between rounded-md border border-border/60 bg-muted/30 px-4 py-3">
                    <div>
                      <p className="text-sm font-medium text-foreground">Show Club Golf Teams</p>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        Include NCCGA club programs in school search and AI recommendations
                      </p>
                    </div>
                    <Switch
                      id="show-club-golf-switch"
                      checked={showClubGolf}
                      onCheckedChange={async checked => {
                        setShowClubGolf(checked);
                        await updateProfileField("showClubGolf", checked);
                      }}
                    />
                  </div>
                </div>
              </CollapsibleContent>
            </Collapsible>

            {/* ── Account ── */}
            <Collapsible
              open={accountOpen}
              onOpenChange={setAccountOpen}
              className="rounded-lg border border-border"
            >
              <CollapsibleTrigger className="flex w-full items-center justify-between px-4 py-3 hover:bg-muted/30 transition-colors [&[data-state=open]>svg:last-child]:rotate-180">
                <div className="flex items-center gap-2">
                  <Settings className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm font-semibold">Account</span>
                </div>
                <div className="flex items-center gap-2">
                  {sectionMissing.account === 0 ? (
                    <Badge variant="secondary" className="text-xs text-primary-text bg-primary/10">
                      Complete
                    </Badge>
                  ) : (
                    <Badge variant="secondary" className="text-xs">
                      {sectionMissing.account} missing
                    </Badge>
                  )}
                  <ChevronDown className="h-4 w-4 text-muted-foreground transition-transform duration-200" />
                </div>
              </CollapsibleTrigger>
              <CollapsibleContent>
                <div className="px-4 pb-4 pt-4 space-y-4 border-t border-border">
                  <p className="text-xs text-muted-foreground">
                    Contact info and account settings.
                  </p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div ref={nameRef}>
                      <AutosaveInput
                        label="Player name"
                        placeholder="Player name"
                        value={displayName}
                        onChange={setDisplayName}
                        allowClear
                        onSave={async v => {
                          await updateProfileField(
                            "displayName",
                            v === null ? null : v,
                            getLinkedJgsProfile()
                          );
                          trackProfileFieldSaved("displayName");
                          refreshUserData();
                        }}
                      />
                      {!isCoachAccount && (
                        <p className="mt-1 text-xs text-muted-foreground">
                          Coaches see this name as the sender of emails you send from
                          golfrecruit.ai.
                        </p>
                      )}
                    </div>
                    <div>
                      <div className="text-xs text-muted-foreground mb-1">
                        Email <ReadOnlyBadge />
                      </div>
                      <Input placeholder="player@example.com" value={email} readOnly disabled />
                    </div>
                  </div>
                  <div ref={cellRef}>
                    <AutosaveInput
                      label="Cell number"
                      placeholder="(555) 123-4567"
                      value={cellNumber}
                      onChange={setCellNumber}
                      allowClear
                      onSave={async v => {
                        await updateProfileField(
                          "cellNumber",
                          v === null ? null : v,
                          getLinkedJgsProfile()
                        );
                        trackProfileFieldSaved("cellNumber");
                      }}
                    />
                  </div>
                  {!isCoachAccount && (
                    <div ref={coachReplyRef}>
                      <AutosaveInput
                        label="Email for coach replies"
                        placeholder={email || "player@example.com"}
                        type="email"
                        value={coachReplyEmail}
                        onChange={setCoachReplyEmail}
                        allowClear
                        validate={value =>
                          /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim())
                            ? null
                            : "Enter a valid email address"
                        }
                        onSave={async v => {
                          await updateProfileField(
                            "coachReplyEmail",
                            v === null ? null : v.trim(),
                            getLinkedJgsProfile()
                          );
                          trackProfileFieldSaved("coachReplyEmail");
                        }}
                      />
                      <p className="mt-1 text-xs text-muted-foreground">
                        When coaches reply to emails you send from golfrecruit.ai, the replies go
                        here. Leave it blank to use {email || "your login email"}.
                      </p>
                    </div>
                  )}
                  <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    <AutosaveInput
                      label="Graduation year"
                      placeholder="2028"
                      value={gradYear}
                      onChange={setGradYear}
                      inputMode="numeric"
                      validate={value => {
                        if (!String(value || "").trim()) return null;
                        const year = Number(value);
                        return Number.isInteger(year) && year >= 2024 && year <= 2035
                          ? null
                          : "Enter a graduation year from 2024 to 2035";
                      }}
                      onSave={async value =>
                        updateProfileField(
                          "gradYear",
                          value ? Number(value) : null,
                          getLinkedJgsProfile()
                        )
                      }
                    />
                    <AutosaveInput
                      label="Home state"
                      placeholder="NC"
                      value={homeState}
                      onChange={setHomeState}
                      onSave={async value =>
                        updateProfileField("state", value || "", getLinkedJgsProfile())
                      }
                    />
                    <label className="space-y-1 text-xs text-muted-foreground">
                      Gender
                      <select
                        value={gender}
                        onChange={async event => {
                          const next = event.target.value;
                          setGender(next);
                          await updateProfileField("gender", next as any, getLinkedJgsProfile());
                        }}
                        className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm text-foreground"
                      >
                        <option value="" disabled>
                          Select gender
                        </option>
                        <option value="male">Male</option>
                        <option value="female">Female</option>
                      </select>
                    </label>
                    <label className="space-y-1 text-xs text-muted-foreground">
                      Who is this set up for?
                      <select
                        value={onboardingRole}
                        onChange={async event => {
                          const next = event.target.value;
                          setOnboardingRole(next);
                          await updateProfileField(
                            "onboardingRole",
                            next as any,
                            getLinkedJgsProfile()
                          );
                        }}
                        className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm text-foreground"
                      >
                        <option value="" disabled>
                          Select one
                        </option>
                        <option value="player">Myself</option>
                        <option value="supporter">A golfer I support</option>
                      </select>
                    </label>
                  </div>
                  {hasPassword === false && (
                    <div className="space-y-2.5">
                      <SectionHeader>Set a Password</SectionHeader>
                      <p className="text-sm text-muted-foreground">
                        Set a password so you can log in directly next time without waiting for an
                        email link.
                      </p>
                      <SetPasswordSection
                        onComplete={() => {
                          setHasPassword(true);
                          refreshUserData();
                        }}
                      />
                    </div>
                  )}
                  {subscription && subscription.stripeCustomerId && (
                    <div className="space-y-2.5 pt-2 border-t">
                      <div className="text-xs font-medium">Subscription</div>
                      <div className="flex items-center justify-between">
                        <div className="text-sm">
                          {subscription.subscriptionState === "active" && (
                            <span className="text-green-600">Active</span>
                          )}
                          {subscription.subscriptionState === "comp" && (
                            <span className="text-green-600">Active (Complimentary)</span>
                          )}
                          {subscription.subscriptionState === "canceling" && (
                            <span className="text-amber-600">
                              Canceling — ends{" "}
                              {subscription.currentPeriodEnd
                                ? new Date(subscription.currentPeriodEnd).toLocaleDateString()
                                : "soon"}
                            </span>
                          )}
                          {subscription.subscriptionState === "canceled" && (
                            <span className="text-red-600">Canceled</span>
                          )}
                          {subscription.subscriptionState === "expired" && (
                            <span className="text-red-600">Expired</span>
                          )}
                          {subscription.subscriptionState === "past_due" && (
                            <span className="text-red-600">Payment Failed</span>
                          )}
                        </div>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={async () => {
                            try {
                              const { url } = await createPortalSession(window.location.href);
                              window.location.href = url;
                            } catch {
                              toast.error("Failed to open billing portal");
                            }
                          }}
                        >
                          Manage Subscription
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              </CollapsibleContent>
            </Collapsible>
          </div>
        </div>
      }
    />
  );
};

export default PlayerProfile;
